import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, ChevronDown, Loader2, MapPin, Package, Plus, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Card, CardContent } from "../../components/ui/card";
import { AddressAutocomplete } from "../../components/customer/AddressAutocomplete";
import { RegisterCalendar } from "../../components/customer/RegisterCalendar";
import { getApiErrorMessage } from "../../lib/apiClient";
import { formatCurrency } from "../../lib/utils";
import { BrandLogo } from "../../components/shared/BrandLogo";
import { RoundLogo } from "../../components/shared/RoundLogo";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;
const STEPS = [
  "Select Plan",
  "Add-Ons",
  "Your Details",
  "Instructions & Schedule",
  "Allergy & Terms",
  "Review & Pay",
];

// Canadian postal code: A1A 1A1 (optional space), case-insensitive.
const CA_POSTAL_RE = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;

const empty = {
  firstname: "", lastname: "", email: "", phone: "", password: "",
  type: "", house_no: "", address: "", city: "", state: "", postal_code: "",
  latitude: "", longitude: "", plan_id: null, start_date: "",
  addon_ids: [], delivery_notes: "", packaging_notes: "",
  allergy_ack: false, tos_ack: false,
};

function earliestStart(blocked = [0, 6]) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  while (blocked.includes(d.getDay())) d.setDate(d.getDate() + 1);
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isBlockedStr(dateStr, blocked = [0, 6]) {
  const day = new Date(`${dateStr}T00:00:00`).getDay();
  return blocked.includes(day);
}

function maxStart() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setMonth(d.getMonth() + 1);
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Required-field label with a red asterisk.
function ReqLabel({ htmlFor, children }) {
  return (
    <Label htmlFor={htmlFor}>
      {children} <span className="text-danger" aria-hidden="true">*</span>
    </Label>
  );
}

// Compact expandable policy section (short notice always visible + accordion).
function PolicySection({ title, policy, checked, onCheck, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border border-border bg-surface p-4" data-testid={`${testid}-section`}>
      <p className="font-semibold text-text-main">
        {title} <span className="text-danger" aria-hidden="true">*</span>
      </p>
      <p className="mt-1 text-sm text-text-muted" data-testid={`${testid}-short`}>
        {policy ? policy.short : "Loading…"}
      </p>
      {policy && (
        <>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="mt-2 flex items-center gap-1 text-sm font-medium text-primary underline"
            data-testid={`${testid}-toggle`}
          >
            {open ? "Hide" : `Read Full ${title}`}
            <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (
            <div
              className="mt-2 max-h-56 overflow-y-auto whitespace-pre-line rounded border border-border bg-background p-3 text-xs leading-relaxed text-text-main"
              data-testid={`${testid}-full`}
            >
              {policy.full}
            </div>
          )}
          <label className="mt-3 flex cursor-pointer items-start gap-2">
            <Checkbox checked={checked} onCheckedChange={(v) => onCheck(!!v)} data-testid={`${testid}-checkbox`} className="mt-0.5" />
            <span className="text-sm text-text-main">
              {policy.checkbox || `I have read and acknowledge the ${title}.`}
            </span>
          </label>
        </>
      )}
    </div>
  );
}

export default function CustomerRegister() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [policies, setPolicies] = useState(null);
  const [preview, setPreview] = useState({ loading: false, end_date: null, price: null, error: null });
  const [summary, setSummary] = useState({ loading: false, data: null, error: null });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [startDateError, setStartDateError] = useState("");
  const [blockedDays, setBlockedDays] = useState([0, 6]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleStartDate = (value) => {
    if (value && isBlockedStr(value, blockedDays)) {
      setStartDateError("New subscriptions can't start on that day — please pick a weekday (Mon–Fri).");
      set({ start_date: "" });
      return;
    }
    if (value && value < earliestStart(blockedDays)) {
      setStartDateError("Start date must be the next day or later — today isn't allowed.");
      set({ start_date: "" });
      return;
    }
    if (value && value > maxStart()) {
      setStartDateError("You can book up to one month in advance.");
      set({ start_date: "" });
      return;
    }
    setStartDateError("");
    set({ start_date: value });
  };

  useEffect(() => {
    axios.get(`${API_BASE}/customer/plans`).then(({ data }) => setPlans(data)).catch(() => {});
    axios.get(`${API_BASE}/customer/addons`).then(({ data }) => setAddons(data)).catch(() => {});
    axios.get(`${API_BASE}/customer/policies`).then(({ data }) => setPolicies(data)).catch(() => {});
    axios.get(`${API_BASE}/customer/delivery-config`)
      .then(({ data }) => { if (Array.isArray(data?.blocked_weekdays)) setBlockedDays(data.blocked_weekdays); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#1A3626";
    return () => { document.body.style.backgroundColor = prev; };
  }, []);

  // Live end-date preview once a plan + start date exist.
  useEffect(() => {
    if (!form.plan_id || !form.start_date) {
      setPreview({ loading: false, end_date: null, price: null, error: null });
      return;
    }
    setPreview((p) => ({ ...p, loading: true, error: null }));
    axios
      .get(`${API_BASE}/customer/plan-preview`, { params: { plan_id: form.plan_id, start_date: form.start_date } })
      .then(({ data }) => setPreview({ loading: false, end_date: data.end_date, price: data.price, error: null }))
      .catch((e) => setPreview({ loading: false, end_date: null, price: null, error: getApiErrorMessage(e, "Could not compute preview.") }));
  }, [form.plan_id, form.start_date]);

  // Server-computed order summary (plan + add-ons + GST) — fetched on the review step.
  useEffect(() => {
    if (step !== 5 || !form.plan_id || !form.start_date) return;
    setSummary({ loading: true, data: null, error: null });
    axios
      .get(`${API_BASE}/customer/order-summary`, {
        params: { plan_id: form.plan_id, start_date: form.start_date, addon_ids: form.addon_ids.join(",") },
      })
      .then(({ data }) => setSummary({ loading: false, data, error: null }))
      .catch((e) => setSummary({ loading: false, data: null, error: getApiErrorMessage(e, "Could not compute your order summary.") }));
  }, [step, form.plan_id, form.start_date, form.addon_ids]);

  const toggleAddon = (id) => {
    setForm((f) => ({
      ...f,
      addon_ids: f.addon_ids.includes(id) ? f.addon_ids.filter((x) => x !== id) : [...f.addon_ids, id],
    }));
  };

  const postalValid = CA_POSTAL_RE.test((form.postal_code || "").trim());

  const canNext = () => {
    if (step === 0) return !!form.plan_id;
    if (step === 1) return true; // add-ons optional
    if (step === 2) return (
      form.firstname && form.lastname && form.email && form.phone && form.password.length >= 6
      && form.type && form.address && form.city && form.state && postalValid
      && form.latitude && form.longitude
    );
    if (step === 3) return !!form.start_date && !!preview.end_date;
    if (step === 4) return form.allergy_ack && form.tos_ack;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        firstname: form.firstname, lastname: form.lastname, email: form.email, phone: form.phone,
        password: form.password, type: form.type, house_no: form.house_no || null,
        address: form.address, city: form.city, state: form.state, postal_code: form.postal_code,
        latitude: form.latitude, longitude: form.longitude, plan_id: form.plan_id,
        start_date: form.start_date, addon_ids: form.addon_ids,
        delivery_notes: form.delivery_notes || null, packaging_notes: form.packaging_notes || null,
        allergy_ack: form.allergy_ack, tos_ack: form.tos_ack,
      };
      const { data } = await axios.post(`${API_BASE}/customer/register`, payload);
      toast.success("Account created — complete payment to activate your plan.");
      navigate(`/pay/${data.payment_link.token}`);
    } catch (e) {
      setError(getApiErrorMessage(e, "Registration failed. Please review your details."));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedPlan = plans.find((p) => p.id === form.plan_id);

  return (
    <div
      className="flex min-h-[100dvh] justify-center bg-primary px-4 py-8"
      style={{ paddingTop: "calc(2rem + env(safe-area-inset-top))", paddingBottom: "calc(2rem + env(safe-area-inset-bottom))" }}
      data-testid="customer-register-page"
    >
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center justify-center gap-3">
          <RoundLogo className="h-20 w-20" />
          <BrandLogo className="h-14" />
        </div>
        <Card className="shadow-2xl">
          <CardContent className="p-6">
            <div className="mb-5 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-text-muted" data-testid="customer-register-step-label">
                Step {step + 1} of {STEPS.length} · {STEPS[step]}
              </p>
            </div>
            <div className="mb-5 flex gap-1">
              {STEPS.map((_, i) => (
                <div key={i} className={`h-1 flex-1 rounded-full ${i <= step ? "bg-primary" : "bg-border"}`} />
              ))}
            </div>

            {/* Step 0 — Plan */}
            {step === 0 && (
              <div className="space-y-3" data-testid="customer-register-step-plan">
                {plans.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => set({ plan_id: p.id })}
                    className={`w-full rounded-md border-2 p-4 text-left transition-colors ${
                      form.plan_id === p.id ? "border-primary bg-primary/5" : "border-border"
                    }`}
                    data-testid={`customer-register-plan-${p.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-text-main">{p.name}</p>
                        <p className="mt-0.5 text-sm text-text-muted">{p.description}</p>
                        <p className="mt-1 text-xs text-text-muted">{p.quantity} tiffins</p>
                      </div>
                      <p className="flex-shrink-0 font-heading text-lg font-bold text-primary">{formatCurrency(p.price)}</p>
                    </div>
                  </button>
                ))}
                {plans.length === 0 && (
                  <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-primary" /></div>
                )}
              </div>
            )}

            {/* Step 1 — Add-Ons */}
            {step === 1 && (
              <div className="space-y-3" data-testid="customer-register-step-addons">
                <div>
                  <p className="font-semibold text-text-main">Choose Your Add-Ons</p>
                  <p className="mt-0.5 text-sm text-text-muted">Optional extras for your tiffin. Select any you'd like, or skip for now.</p>
                </div>
                {addons.map((a) => {
                  const active = form.addon_ids.includes(a.id);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => toggleAddon(a.id)}
                      className={`flex w-full items-center justify-between gap-2 rounded-md border-2 p-3 text-left transition-colors ${
                        active ? "border-primary bg-primary/5" : "border-border"
                      }`}
                      data-testid={`customer-register-addon-${a.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border ${active ? "border-primary bg-primary text-white" : "border-border"}`}>
                          {active ? <span className="text-xs">✓</span> : <Plus size={12} className="text-text-muted" />}
                        </span>
                        <span className="font-medium text-text-main">{a.name}</span>
                      </div>
                      <span className="flex-shrink-0 font-semibold text-primary">{formatCurrency(a.price)}</span>
                    </button>
                  );
                })}
                {addons.length === 0 && <p className="py-4 text-center text-sm text-text-muted">No add-ons available right now.</p>}
                {form.addon_ids.length > 0 && (
                  <p className="text-sm text-text-muted" data-testid="customer-register-addons-selected">
                    {form.addon_ids.length} add-on{form.addon_ids.length > 1 ? "s" : ""} selected.
                  </p>
                )}
              </div>
            )}

            {/* Step 2 — Your Details & Delivery Address */}
            {step === 2 && (
              <div className="space-y-3" data-testid="customer-register-step-details">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <ReqLabel htmlFor="firstname">First name</ReqLabel>
                    <Input id="firstname" data-testid="customer-register-firstname-input" value={form.firstname} onChange={(e) => set({ firstname: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <ReqLabel htmlFor="lastname">Last name</ReqLabel>
                    <Input id="lastname" data-testid="customer-register-lastname-input" value={form.lastname} onChange={(e) => set({ lastname: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <ReqLabel htmlFor="email">Email</ReqLabel>
                  <Input id="email" type="email" data-testid="customer-register-email-input" value={form.email} onChange={(e) => set({ email: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <ReqLabel htmlFor="phone">Phone</ReqLabel>
                  <Input id="phone" data-testid="customer-register-phone-input" value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <ReqLabel htmlFor="password">Password</ReqLabel>
                  <Input id="password" type="password" data-testid="customer-register-password-input" value={form.password} onChange={(e) => set({ password: e.target.value })} />
                  <p className="text-xs text-text-muted">At least 6 characters.</p>
                </div>

                <div className="space-y-1.5 pt-1">
                  <ReqLabel>Delivery type</ReqLabel>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: "pickup", title: "Pickup" },
                      { key: "delivery", title: "Delivery" },
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => set({ type: opt.key })}
                        className={`rounded-md border-2 p-3 text-center text-sm font-medium transition-colors ${
                          form.type === opt.key ? "border-primary bg-primary/5 text-text-main" : "border-border text-text-muted"
                        }`}
                        data-testid={`customer-register-type-${opt.key}`}
                      >
                        {opt.title}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5 pt-1">
                  <ReqLabel>Street address</ReqLabel>
                  <AddressAutocomplete
                    value={form.address}
                    onChange={(v) => set({ address: v })}
                    onSelect={(r) =>
                      set({
                        address: r.address || form.address, city: r.city || "", state: r.state || "",
                        postal_code: r.postal_code || "", latitude: r.latitude || "", longitude: r.longitude || "",
                      })
                    }
                  />
                  {!form.latitude && (
                    <p className="flex items-center gap-1.5 text-xs text-warning" data-testid="customer-register-address-hint">
                      <MapPin size={13} strokeWidth={1.5} /> Pick a suggested address above so we can map your location.
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="house_no">Apt / Unit <span className="text-text-muted">(optional)</span></Label>
                  <Input id="house_no" data-testid="customer-register-house-no-input" value={form.house_no} onChange={(e) => set({ house_no: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <ReqLabel htmlFor="city">City</ReqLabel>
                    <Input id="city" data-testid="customer-register-city-input" value={form.city} onChange={(e) => set({ city: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <ReqLabel htmlFor="state">Province</ReqLabel>
                    <Input id="state" data-testid="customer-register-state-input" value={form.state} onChange={(e) => set({ state: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <ReqLabel htmlFor="postal_code">Postal Code</ReqLabel>
                  <Input id="postal_code" data-testid="customer-register-postal-input" value={form.postal_code} onChange={(e) => set({ postal_code: e.target.value })} />
                  {form.postal_code && !postalValid && (
                    <p className="text-xs text-danger" data-testid="customer-register-postal-error">
                      Enter a valid Canadian postal code (e.g. A1A 1A1).
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Step 3 — Instructions & Schedule */}
            {step === 3 && (
              <div className="space-y-4" data-testid="customer-register-step-instructions">
                <div className="space-y-1.5">
                  <ReqLabel>Start date</ReqLabel>
                  <RegisterCalendar
                    value={form.start_date}
                    min={earliestStart(blockedDays)}
                    max={maxStart()}
                    blockedDays={blockedDays}
                    onChange={handleStartDate}
                  />
                  <p className="text-xs text-text-muted">New subscriptions start the next day at the earliest, up to one month ahead. Weekends are greyed out.</p>
                  {startDateError && <p className="text-sm text-danger" data-testid="customer-register-start-date-error">{startDateError}</p>}
                  {preview.loading && <Loader2 size={18} className="animate-spin text-primary" />}
                  {preview.end_date && (
                    <div className="rounded-md border border-border bg-surface p-3" data-testid="customer-register-preview">
                      <p className="text-sm text-text-main">
                        <span className="font-semibold">{selectedPlan?.quantity} {selectedPlan?.quantity === 1 ? "tiffin" : "tiffins"}</span> — expected to finish around <span className="font-semibold">{preview.end_date}</span>
                      </p>
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="delivery_notes">Delivery Instructions <span className="text-text-muted">(optional)</span></Label>
                  <Textarea
                    id="delivery_notes"
                    data-testid="customer-register-delivery-notes-input"
                    rows={3}
                    placeholder="Example: Leave my tiffin at the front door. Please do not ring the doorbell."
                    value={form.delivery_notes}
                    onChange={(e) => set({ delivery_notes: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="packaging_notes">Packing Instructions <span className="text-text-muted">(optional)</span></Label>
                  <Textarea
                    id="packaging_notes"
                    data-testid="customer-register-packaging-notes-input"
                    rows={3}
                    placeholder="Example: Please pack rotis separately."
                    value={form.packaging_notes}
                    onChange={(e) => set({ packaging_notes: e.target.value })}
                  />
                </div>
              </div>
            )}

            {/* Step 4 — Allergy & Terms */}
            {step === 4 && (
              <div className="space-y-4" data-testid="customer-register-step-policies">
                <PolicySection
                  title="Food Allergy Warning"
                  policy={policies?.allergy}
                  checked={form.allergy_ack}
                  onCheck={(v) => set({ allergy_ack: v })}
                  testid="customer-register-allergy"
                />
                <PolicySection
                  title="Terms of Service"
                  policy={policies?.terms}
                  checked={form.tos_ack}
                  onCheck={(v) => set({ tos_ack: v })}
                  testid="customer-register-tos"
                />
                {!canNext() && (
                  <p className="text-xs text-text-muted">Please acknowledge both to continue.</p>
                )}
              </div>
            )}

            {/* Step 5 — Review & Pay */}
            {step === 5 && (
              <div className="space-y-3" data-testid="customer-register-step-review">
                <div className="rounded-md border border-border bg-surface p-4 text-sm text-text-main">
                  <p className="flex items-center gap-1.5 font-semibold"><UserIcon size={14} strokeWidth={1.5} /> {form.firstname} {form.lastname}</p>
                  <p className="mt-1 text-text-muted">{form.email} · {form.phone}</p>
                  <p className="mt-2 flex items-center gap-1.5"><MapPin size={14} strokeWidth={1.5} /> {form.house_no ? `${form.house_no}, ` : ""}{form.address}, {form.city}, {form.state} {form.postal_code}</p>
                  <p className="mt-2 flex items-center gap-1.5 capitalize"><Package size={14} strokeWidth={1.5} /> {form.type} plan · starts {form.start_date}</p>
                </div>

                {summary.loading && <div className="flex justify-center py-4"><Loader2 size={20} className="animate-spin text-primary" /></div>}
                {summary.error && <p className="text-sm text-danger" data-testid="customer-register-summary-error">{summary.error}</p>}
                {summary.data && (
                  <div className="rounded-md border border-border bg-surface p-4 text-sm" data-testid="customer-register-order-summary">
                    <div className="flex justify-between">
                      <span className="text-text-main">{summary.data.plan_name}</span>
                      <span className="text-text-main">{formatCurrency(summary.data.plan_price)}</span>
                    </div>
                    <p className="mt-0.5 text-xs text-text-muted">{summary.data.quantity} tiffins · ends ~{summary.data.end_date}</p>
                    {summary.data.addons.map((a) => (
                      <div key={a.id} className="mt-1.5 flex justify-between text-text-muted">
                        <span>+ {a.name}</span>
                        <span>{formatCurrency(a.price)}</span>
                      </div>
                    ))}
                    <div className="my-2 border-t border-border" />
                    <div className="flex justify-between text-text-muted">
                      <span>Subtotal</span><span>{formatCurrency(summary.data.subtotal)}</span>
                    </div>
                    {summary.data.gst_enabled && (
                      <div className="flex justify-between text-text-muted">
                        <span>{summary.data.gst_label} ({summary.data.gst_rate}%)</span>
                        <span>{formatCurrency(summary.data.gst_amount)}</span>
                      </div>
                    )}
                    <div className="mt-2 flex justify-between border-t border-border pt-2">
                      <span className="font-semibold text-text-main">Total</span>
                      <span className="font-heading text-xl font-bold text-primary" data-testid="customer-register-review-total">
                        {formatCurrency(summary.data.total)}
                      </span>
                    </div>
                  </div>
                )}
                {error && <p className="text-sm text-danger" data-testid="customer-register-error">{error}</p>}
                <p className="text-xs text-text-muted">You'll pay online on the next screen to activate your plan right away.</p>
              </div>
            )}

            <div className="mt-6 flex gap-2">
              {step > 0 && (
                <Button variant="secondary" className="flex-1" onClick={() => setStep((s) => s - 1)} data-testid="customer-register-back-button">
                  <ArrowLeft size={16} strokeWidth={1.5} /> Back
                </Button>
              )}
              {step === 1 ? (
                <>
                  <Button variant="outline" className="flex-1" onClick={() => { set({ addon_ids: [] }); setStep((s) => s + 1); }} data-testid="customer-register-skip-addons-button">
                    Skip for Now
                  </Button>
                  <Button className="flex-1" onClick={() => setStep((s) => s + 1)} data-testid="customer-register-next-button">
                    Continue <ArrowRight size={16} strokeWidth={1.5} />
                  </Button>
                </>
              ) : step < STEPS.length - 1 ? (
                <Button className="flex-1" disabled={!canNext()} onClick={() => setStep((s) => s + 1)} data-testid="customer-register-next-button">
                  Next <ArrowRight size={16} strokeWidth={1.5} />
                </Button>
              ) : (
                <Button className="flex-1" disabled={submitting || summary.loading || !summary.data} onClick={submit} data-testid="customer-register-submit-button">
                  {submitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                  Complete & Pay
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        <p className="mt-4 text-center text-xs text-white/70">
          Already have an account?{" "}
          <button type="button" className="underline" onClick={() => navigate("/customer/login")} data-testid="customer-register-login-link">
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
}
